import {
  Button,
  Card,
  CardBody,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/solid';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { IdentityType, RepresentativeRole, useGetCompanyQuery } from '@blockpulse3/graphql/hooks';
import {
  ErrorQueryCard,
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { canUserUpdateCompany } from './utils';

type Props = {
  onOpen: () => void;
};

export function SettingsCompanyAccess({ onOpen }: Props): JSX.Element {
  const i18nWorkspaceSettings = useTranslations('WorkspaceSettings');
  const i18nRoleValues = useTranslations('RoleValues');
  const i18nOtherRole = useTranslations('OtherRole');

  const { user } = useUser();
  const { companyId = '' } = useParams();
  const { data, loading, error } = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });

  if (error || !data) {
    return (
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: 5, lg: 8 }}>
        <Text flexBasis={{ lg: 250 }} fontSize="lg" fontWeight="medium">
          {i18nWorkspaceSettings('RepresentativesTitle')}
        </Text>
        <ErrorQueryCard h="100px" maxW={{ lg: '3xl' }} w="full" />
      </Stack>
    );
  }

  // Filter representatives to keep only VIEWER and CREATOR not already displayed
  const representatives = data?.company?.companyRepresentative || [];
  const representativeIds = representatives
    .filter((rep) => ![RepresentativeRole.CREATOR, RepresentativeRole.VIEWER].includes(rep.role))
    .map((rep) => rep.representativeIdentity.id);
  const accessReps = representatives
    .filter(
      (rep) =>
        rep.role === RepresentativeRole.VIEWER ||
        !representativeIds.includes(rep.representativeIdentity.id),
    )
    .sort((a, b) => (a.representativeIdentity.name > b.representativeIdentity.name ? 1 : -1));

  const isEditDisabled = !canUserUpdateCompany(user, data);

  return (
    <Stack
      alignItems="flex-start"
      direction={{ base: 'column', lg: 'row' }}
      spacing={{ base: 5, lg: 8 }}
    >
      <HStack alignItems="center" flexBasis={{ lg: 250 }}>
        <Text fontSize="lg" fontWeight="medium">
          {i18nWorkspaceSettings('OtherRolesTitle')}
        </Text>
      </HStack>
      <Skeleton isLoaded={!loading} maxWidth={{ lg: '3xl' }} width="full">
        <Card maxW={{ lg: '3xl' }} variant="divider-bottom">
          <CardBody>
            <Stack spacing="4">
              {accessReps.length === 0 && (
                <Text color="gray.500">{i18nWorkspaceSettings('NoOtherRoles')}</Text>
              )}
              {accessReps.map((representative) => {
                const identity = representative.representativeIdentity;
                const address =
                  identity.type === IdentityType.COMPANY
                    ? identity.companyIdentity?.address?.formatted
                    : identity.individualIdentity?.address?.formatted;

                return (
                  <Stack
                    key={representative.id}
                    alignItems="flex-start"
                    direction={['column', 'row']}
                    spacing="2"
                  >
                    <Heading flexBasis="150px" size="sm">
                      {i18nRoleValues(representative.role as keyof IntlMessages['RoleValues'])}
                      {representative.isContact && (
                        <Text fontSize="xs">({i18nOtherRole('Contact')})</Text>
                      )}
                    </Heading>
                    <IdentityCard>
                      <IdentityAvatar
                        boxSize="9"
                        src={identity.profilePicture}
                        type={identity.type}
                      />
                      <Stack spacing="0">
                        <IdentityCardTitle fontSize="md" isChecked={identity.isVerified}>
                          <Text>{identity.name}</Text>
                        </IdentityCardTitle>
                        <IdentityCardDescription>
                          <Text>{address}</Text>
                          <Text>{identity.email}</Text>
                          <Text>{identity.phone}</Text>
                        </IdentityCardDescription>
                      </Stack>
                    </IdentityCard>
                  </Stack>
                );
              })}
              <Button isDisabled={isEditDisabled} variant="secondary" onClick={onOpen}>
                <Icon as={PencilIcon} mr="1" /> {i18nWorkspaceSettings('Edit')}
              </Button>
            </Stack>
          </CardBody>
        </Card>
      </Skeleton>
    </Stack>
  );
}

export type SettingsCompanyAccessProps = Props;
